import { localStorage } from 'bv';
import { send as eventBusSend } from 'event-bus';
import { set as lsSet } from 'bv-local-storage';
import { addOutcomes, removeOutcomes, updateOutcome } from 'Sportsbook/outcomes/duck';
import outcomePresenter from 'Sportsbook/outcomes/presenter';
import {
  getBetslipSingles,
  getBetslipMultiples,
  getBetslipOutcomeIds,
  getStakes,
  getStake,
  hasInRunningBet,
  getUsePromocash,
  getStatus,
  getAcceptAllPriceChangeToggleValue,
  getCurrentTab,
} from './selector';
import {
  addToBetslipInit,
  addToBetslipSuccess,
  multiAddToBetslipInit,
  removeFromBetslipInit,
  removeFromBetslipSuccess,
  resetBetslipSuccess,
  setBetDelay,
  betslipSubmitInit,
  betslipSubmitSuccess,
  updateBetslipPrices,
  setStakes,
  setStakeValue,
  emptyBetslip,
  clearMultipleStakes,
  betslipSetTab,
  betslipSetStatus,
  BETSLIP_STATUS,
} from './duck';

import {
  getBetDelay,
  postBetslipSubmit,
  addToBetslipRequest,
  removeFromBetslipRequest,
  fetchPrices,
  updatePriceRequest,
  multipleAddToBetslipRequest,
  followLineChangesRequest,
  betslipFetch,
} from './api';
import * as dataLayer from './data_layer';
import { generatePriceDeltas, validatePriceChanges } from './helpers';

let lineChangeFollowRequest = Promise.resolve();

const getOutcomeIdsFromShowResponse = (resp) => resp.singles.map((single) => single.outcome_id);

// when an issue returns for an EW selection for multiples
// it references the each_way_win_bet_type_id in the error, while
// we store the stake info by win_bet_type_id, so to produce the correct
// stakeId we need to add that back into the issue.issue from the
// attached multiple it comes with
const getStakeId = ({ issue, multiple }) => {
  const betTypeId = multiple ? multiple.win_bet_type_id : null;
  return (issue.outcome ? `s-${issue.outcome}` : `m-${betTypeId}`);
};

const refreshOutcomesFromEventBus = (outcomeIds, dispatch) => {
  if (outcomeIds.length === 0) return;
  eventBusSend('get/outcome', { payload: { ids: outcomeIds } }, (err, outcomesResp) => {
    if (err) return;
    if (!outcomesResp.body || !outcomesResp.body.result) return;

    outcomesResp.body.result.forEach((outcomeData) => {
      dispatch(updateOutcome(outcomeData.oid, outcomeData));
    });
  });
};

export const addToBetslip = (outcomeId, opts) => (dispatch, getState) => {
  const state = getState();
  const outcome = state.outcomes[outcomeId];
  if (!outcome) return null;
  dispatch(addToBetslipInit(outcomeId, opts));

  const market = Object.values(state.markets || {}).find((m) => m.o.includes(outcomeId)) || {};

  dataLayer.pushAddOutcomeEvent(outcomeId);
  dispatch(addOutcomes([{ id: outcomeId }]));

  return addToBetslipRequest({
    outcome: outcomePresenter(outcome),
    market,
    opts,
  }).then((resp) => {
    dispatch(addToBetslipSuccess(getOutcomeIdsFromShowResponse(resp), resp));
    refreshOutcomesFromEventBus([outcomeId], dispatch);
  });
};

export const addMultipleToBetslip = (outcomeIds) => (dispatch) => new Promise((resolve, reject) => {
  dispatch(addOutcomes(outcomeIds.map((id) => ({ id }))));
  dispatch(multiAddToBetslipInit(outcomeIds));

  multipleAddToBetslipRequest({ outcomeIds }).then((resp) => {
    dispatch(addToBetslipSuccess(getOutcomeIdsFromShowResponse(resp), resp));
    refreshOutcomesFromEventBus(outcomeIds, dispatch);
    resolve();
  }).catch(() => {
    reject();
  });
});

export const removeFromBetslip = (outcomeId) => (dispatch, getState) => {
  dispatch(removeFromBetslipInit(outcomeId));
  return removeFromBetslipRequest(outcomeId).then((resp) => {
    // Rapid fire remove requests and buggy sessions can result
    // in an outcome delete request to be sent and the result still
    // containing that outcome we tried to delete.
    // in this case lowering the ref number could lead to bugs, so check the
    // payload if the outcome was, in-fact removed
    const state = getState();
    const newOutcomeIds = getOutcomeIdsFromShowResponse(resp);
    const oldOutcomeIds = getBetslipOutcomeIds(state);
    const removedOutcomeIds = oldOutcomeIds.filter((o) => !newOutcomeIds.includes(o));
    if (removedOutcomeIds.length > 0) {
      dispatch(removeOutcomes(removedOutcomeIds));
      removedOutcomeIds.forEach((oId) => {
        dataLayer.pushRemoveOutcomeEvent(oId, state);
      });
    }

    dispatch(removeFromBetslipSuccess(newOutcomeIds, resp));
    // if for some reason there's a new outcome in the betslip
    // that we didn't see before (session weirdness) refresh it's status and price
    refreshOutcomesFromEventBus(newOutcomeIds.filter((o) => !oldOutcomeIds.includes(o)), dispatch);

    // clear stakes for multiples, we can't be sure if they are the same bet
    // as before the addition
    dispatch(clearMultipleStakes());
  });
};

export const resetBetslip = () => (dispatch, getState) => {
  dispatch(removeOutcomes(getBetslipOutcomeIds(getState()) || []));

  return betslipFetch().then((resp) => {
    const newOutcomeIds = getOutcomeIdsFromShowResponse(resp);
    dispatch(addOutcomes(newOutcomeIds.map((id) => ({ id }))));
    dispatch(resetBetslipSuccess(newOutcomeIds, resp));
    refreshOutcomesFromEventBus(newOutcomeIds, dispatch);
    // clear stakes for multiples, we can't be sure if they are the same bet
    // as before the deletion
    dispatch(clearMultipleStakes());
  });
};

export const makeIssueHandler = (dispatch) => (issue) => {
  const stakeId = getStakeId(issue);
  switch (issue.issue.type) {
    case 'price_change':
      // multiple price changes are not handled here,
      // only way a price of a multiple could change if it's singles' change
      // triggering an outcome update when we have multiples on the betslip
      // will lead to a price-fetch and they will be updated from there
      if (issue.issue.bet_type === 'single') {
        dispatch(updateOutcome(issue.issue.outcome, {
          prid: issue.issue.new_price_id,
          prd: issue.issue.new_price,
          pr: issue.issue.new_price_text_formatted,
        }));
      }
      break;

    case 'reoffered_stake':
      dispatch(setStakeValue(stakeId, issue.issue.stake || 0));
      break;

    case 'suspended':
      dispatch(updateOutcome(issue.issue.outcome, {
        os: 2,
      }));
      break;

    case 'bet_attendant_rejected':
      dispatch(setStakeValue(stakeId, 0));
      break;

    default:
      break;
  }
};

export const submitBetslip = (
  betslipType, enhancedOutcomesById, filters,
) => (dispatch, getState) => {
  dispatch(betslipSubmitInit({ acceptPriceChanges: true }));

  const state = getState();
  const { showResponse } = state.betslip;
  const stakes = getStakes(state);
  const usePromocash = getUsePromocash(state);

  const betDelayRequest = hasInRunningBet(state) ? getBetDelay() : Promise.resolve(0);
  const issueHandler = makeIssueHandler(dispatch);

  return betDelayRequest.then((delay) => {
    if (delay > 0) {
      dispatch(setBetDelay(delay));
      return new Promise((resolve) => {
        window.setTimeout(() => resolve(delay), delay * 1000);
      });
    }
    return Promise.resolve(0);
  }).then((delay) => {
    // re-check if the submit process was not cancelled by a price-change during the delay
    // if there wasn't any price-change the status is still should be DELAY
    const status = getStatus(getState());
    const { outcomes } = getState();
    if (delay > 0 && status !== BETSLIP_STATUS.DELAY) {
      throw new Error();
    }

    // make sure if there's a pending line-follow, we wait for it to be finished before submitting
    return lineChangeFollowRequest.then(() => {
      dispatch(betslipSubmitInit({ acceptPriceChanges: false }));

      return postBetslipSubmit({
        showResponse,
        enhancedOutcomesById,
        stakes,
        usePromocash,
        betslipType,
        outcomes,
        ...filters,
      });
    }).then((submitResponse) => {
      let submitResponsePromise = null;
      if (submitResponse.success) {
        Object.keys(stakes).forEach((key) => localStorage.remove(`stakes-v2-${key}`));
        refreshOutcomesFromEventBus(submitResponse.outcomes.allIds, dispatch);
        lsSet('usePromocash', false);
        dataLayer.pushBetSubmitEvent(submitResponse, betslipType === 'mini-betslip' ? 'quick_bet' : 'betslip');
        return dispatch(betslipSubmitSuccess(submitResponse));
      }
      switch (submitResponse.type) {
        case 'timeout_required':
          // this should not be happening, but it does because rails
          // session handling loses changes, just-re-run the submission if it happens.
          return dispatch(submitBetslip(betslipType, enhancedOutcomesById, filters));

        case 'review':
          // set betslip status back to READY, finish the submission state
          // take per bet-item problems, run the handlers per issue and save the
          // issues indexed by stake-id for the UI to display them later and throw
          // away the submit-response
          submitResponsePromise = dispatch(betslipSubmitSuccess(null, Object.fromEntries(
            submitResponse.issues.map((issue) => [getStakeId(issue), issue.issue]),
          )));
          submitResponse.issues.forEach(issueHandler);
          return submitResponsePromise;

        // just store the result and frontend
        // will render the correct dialog for the type on the UI
        default:
          return dispatch(betslipSubmitSuccess(submitResponse));
      }
    }).catch(() => null, // used by cancelled delay by a price change, noop
    );
  });
};

export const updatePrices = (updatedOutcome) => (dispatch, getState) => {
  const state = getState();
  const status = getStatus(getState());
  // during submission, we need to ignore all price-changes,
  // unless they are about outcomes with mblDistance
  // we'll run a `send get/outcomes` on submit-success to catch-up with the prices
  if (status === BETSLIP_STATUS.SUBMIT && !updatedOutcome.mblDistance) return null;

  const singles = getBetslipSingles(state);
  const multiples = getBetslipMultiples(state);
  const acceptPriceChangesPct = getAcceptAllPriceChangeToggleValue(state);

  const updatedSingle = singles.find((single) => single.outcomeId === updatedOutcome.id);
  const shouldFetchPrice = updatedSingle.hasEW || multiples.length > 0;

  if (shouldFetchPrice) {
    // when asking for new prices for ew / multiples,
    // need to use the new price on the updated single
    const updatedSingles = singles.map(
      (single) => (single.outcomeId === updatedSingle.outcomeId
        ? { ...single, decimalPrice: updatedOutcome.prd }
        : single
      ),
    );
    return fetchPrices(updatedSingles, multiples)
      .then((prices) => validatePriceChanges(updatedSingles, prices))
      .then((prices) => {
        dispatch(updateBetslipPrices(
          ...generatePriceDeltas(updatedOutcome, singles, multiples, prices), acceptPriceChangesPct,
        ));

        // update data in session if there was a change, response is empty, we can ignore it
        const single = singles.find((s) => s.outcomeId === updatedOutcome.id);
        if (single.priceId !== updatedOutcome.prid) {
          updatePriceRequest(updatedOutcome.id, updatedOutcome.prid);
        }
      })
      .catch(() => null, // if validation fails we ignore the response
      );
  }

  // update the one single with the price from the original push message,
  // no need to talk to the backend
  dispatch(updateBetslipPrices({
    [updatedSingle.outcomeId]: {
      priceId: updatedOutcome.prid,
      decimalPrice: updatedOutcome.prd,
      textFormattedPrice: updatedOutcome.pr,
      mblDistance: updatedOutcome.mblDistance,
      oldMblDistance: [null, undefined].includes(updatedSingle.mblDistance)
        ? updatedOutcome.mblDistance
        : updatedSingle.mblDistance,
    },
  }, {}, acceptPriceChangesPct));
  return null;
};

export const clearBetslip = () => (dispatch) => {
  lsSet('usePromocash', false);
  return dispatch(emptyBetslip({ clearSubmitResponse: true }));
};

export const followLineChanges = (outcomeDistancePairs) => (dispatch, getState) => {
  lineChangeFollowRequest = lineChangeFollowRequest.then(() => {
    const oldState = getState();
    const oldBetslip = oldState.betslip;
    const oldStatus = oldBetslip.status;

    // if we are already subitting when line changes, ignore the change
    if (oldStatus === BETSLIP_STATUS.SUBMIT) return null;

    // if there was a failed submit, don't change the lines anymore
    // so issues with submission won't disappear
    if (oldBetslip.submitResponse
      || (oldBetslip.betIssues && Object.keys(oldBetslip.betIssues).length > 0)) {
      return null;
    }

    dispatch(betslipSetStatus(BETSLIP_STATUS.FETCH));
    return followLineChangesRequest(outcomeDistancePairs).then((resp) => {
      const newOutcomeIds = getOutcomeIdsFromShowResponse(resp);
      // backend can fail, we should ignore an empty response
      if (newOutcomeIds.length === 0) {
        dispatch(betslipSetStatus(oldStatus));
        return;
      }
      dispatch(removeOutcomes(Object.keys(outcomeDistancePairs)));
      dispatch(addOutcomes(newOutcomeIds.map((id) => ({ id }))));

      // reset the status to what it was before this could be either READY or DELAY.
      // we need to do this so a line-change doesn't stop the countdown to submit
      // console.log(`setting betslip back to ${oldStatus}`);
      dispatch(resetBetslipSuccess(newOutcomeIds, resp, oldStatus));
      refreshOutcomesFromEventBus(newOutcomeIds, dispatch);

      resp.singles.forEach((single) => {
        if (!single.old_outcome_id) return;

        const oldStakeId = `s-${single.old_outcome_id}`;
        const stakeId = `s-${single.outcome_id}`;
        const oldStake = getStake(oldState, oldStakeId);
        localStorage.remove(`stakes-v2-${oldStakeId}`);
        dispatch(setStakes({ [stakeId]: oldStake }));
      });
    });
  });
  return lineChangeFollowRequest;
};

export const updateSelectedBetslipTab = (
  currentTabSelectedManually, showResponse,
) => (dispatch, getState) => {
  const state = getState();
  const currentTab = getCurrentTab(state);
  const tabStates = {
    singles: showResponse && showResponse.singles > 0,
    acca: showResponse && showResponse.multiples.filter((m) => m.multiplicity === 1).length > 0,
    multiples: showResponse && showResponse.multiples.filter((m) => m.multiplicity > 1).length > 0,
  };

  let newTab = '';

  if (currentTabSelectedManually) {
    if (tabStates[currentTab]) {
      newTab = currentTab;
    } else if (currentTab === 'multiples' && tabStates.acca) {
      // if you were on multiples tab (can only get there by manual selection)
      // and now it's unavailable (removed an outcome) the fallback should be the
      // acca tab instead of singles
      newTab = 'acca';
    } else {
      newTab = 'singles';
    }
  } else if (!currentTabSelectedManually) {
    if (tabStates.acca) {
      // give preference to acca tab if it's available and the tabs have not been
      // interacted with before
      newTab = 'acca';
    } else {
      newTab = 'singles';
    }
  }

  dispatch(betslipSetTab(newTab, false));
};
